import { Combobox } from '@base-ui/react/combobox';
import styled from '@emotion/styled';

import {
  focusRing,
  hoverable,
  IconButton,
  pressable,
  scrollbarStyles,
  theme,
  Typography,
  zIndex,
} from '@/design-system';
import { typographyVariantStyles } from '@/design-system/typography-variants';

export const Root = styled('div')(({ theme: currentTheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: currentTheme.spacing[4],
  width: '100%',
}));

export const Field = styled(Combobox.InputGroup, {
  shouldForwardProp: (prop) => !['$invalid', '$disabled'].includes(prop as string),
})<{ $invalid: boolean; $disabled: boolean }>(({ theme: currentTheme, $invalid, $disabled }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: currentTheme.spacing[8],
  width: '100%',
  minWidth: 0,
  height: currentTheme.spacing[48],
  minHeight: currentTheme.spacing[48],
  boxSizing: 'border-box',
  paddingInline: currentTheme.spacing[12],
  border: `1px solid ${$invalid ? currentTheme.colors.error.border : currentTheme.colors.border.default}`,
  borderRadius: currentTheme.radius[12],
  backgroundColor: currentTheme.colors.background.overlay,
  color: currentTheme.colors.text.primary,
  ...hoverable(currentTheme, $invalid ? 'surfaceStrong' : 'border'),
  ...focusRing(currentTheme, { within: true }),
  ...($disabled && {
    opacity: 0.5,
    pointerEvents: 'none',
  }),
}));

export const LeadingIcon = styled('span')(({ theme: currentTheme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 16,
  height: 16,
  color: currentTheme.colors.icon.primary,

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

export const Input = styled(Combobox.Input)(({ theme: currentTheme }) => ({
  flex: 1,
  width: '100%',
  minWidth: 0,
  height: 42,
  padding: 0,
  border: 0,
  outline: 0,
  appearance: 'none',
  backgroundColor: 'transparent',
  color: currentTheme.colors.text.primary,
  fontFamily: 'inherit',
  ...typographyVariantStyles.body2,

  '&::placeholder': {
    color: currentTheme.colors.text.secondary,
    opacity: 1,
  },

  '&:disabled': {
    cursor: 'not-allowed',
  },
}));

export const SelectedInput = styled(Input)({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export const Positioner = styled(Combobox.Positioner)({
  zIndex: zIndex.dropdown,
});

export const Popup = styled(Combobox.Popup)(({ theme: currentTheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: 'var(--anchor-width)',
  maxWidth: 'var(--available-width)',
  maxHeight: 'var(--available-height)',
  boxSizing: 'border-box',
  padding: currentTheme.spacing[4],
  overflow: 'hidden',
  border: `1px solid ${currentTheme.colors.border.default}`,
  borderRadius: currentTheme.radius[8],
  outline: 'none',
  backgroundColor: currentTheme.colors.background.raised,
  boxShadow: currentTheme.shadows.md,
  transformOrigin: 'var(--transform-origin)',
  transition: 'opacity 0.15s ease, transform 0.15s ease',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.97) translateY(-4px)',
  },
}));

export const List = styled(Combobox.List)({
  maxHeight: 320,
  overflowY: 'auto',
  ...scrollbarStyles,
});

export const Option = styled(Combobox.Item)(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: currentTheme.spacing[8],
  minHeight: 48,
  boxSizing: 'border-box',
  padding: currentTheme.spacing[8],
  borderRadius: currentTheme.radius[8],
  outline: 'none',
  color: currentTheme.colors.text.primary,
  cursor: 'pointer',
  userSelect: 'none',
  ...hoverable(currentTheme),
  ...pressable(currentTheme),
  ...focusRing(currentTheme),

  '&[data-highlighted]': {
    backgroundColor: currentTheme.colors.background.overlay,
  },

  '&[data-selected]': {
    color: currentTheme.colors.text.brand,
  },
}));

export const AvatarImage = styled('img')(({ theme: currentTheme }) => ({
  display: 'block',
  width: '100%',
  height: '100%',
  borderRadius: currentTheme.radius.full,
  objectFit: 'cover',
}));

export const ItemContent = styled('span')({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  minWidth: 0,
});

export const ItemDetailRow = styled('span')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: currentTheme.spacing[8],
  minWidth: 0,
}));

export const TruncatedText = styled(Typography)({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StatusRow = styled('div')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: currentTheme.spacing[8],
  minHeight: 48,
  padding: currentTheme.spacing[8],
  boxSizing: 'border-box',
  textAlign: 'center',
}));

export const LiveStatus = styled(Combobox.Status)({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
});

export const SelectedContent = styled('div')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: currentTheme.spacing[4],
  flex: 1,
  minWidth: 0,
}));

export const SelectedIdentityButton = styled('button')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  gap: currentTheme.spacing[8],
  padding: 0,
  border: 0,
  backgroundColor: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  ...focusRing(currentTheme),
}));

export const SelectedIdentity = styled('span')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  flex: '0 1 auto',
  minWidth: 0,
  gap: currentTheme.spacing[12],
}));

export const SelectedName = styled(Typography)({
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '100%',
});

export const SelectedAddress = styled(TruncatedText)({
  flex: '0 1 auto',
});

export const Actions = styled('div')(({ theme: currentTheme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: currentTheme.spacing[4],
  flexShrink: 0,
}));

export const ActionButton = styled(IconButton)({
  minWidth: 44,
  minHeight: 44,
});

export const SelectedField = styled(Field)({
  height: theme.spacing[48],
  minHeight: theme.spacing[48],
});
